import { useEffect, useState } from "react";
import api from "@/lib/api";
import StatCard from "@/components/StatCard";
import { useNavigate } from "react-router-dom";
import { useRoleBase } from "@/lib/roles";
import { CheckCircle2, Timer, Truck, AlertTriangle, MessageSquareText, Zap, Hourglass, CalendarClock, BarChart3 } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell } from "recharts";

export default function Analytics() {
  const [data, setData] = useState(null);
  const navigate = useNavigate();
  const base = useRoleBase();

  useEffect(() => { api.get("/analytics").then((r) => setData(r.data)).catch(() => {}); }, []);
  if (!data) return <div className="py-20 text-center text-sm text-muted-foreground">Computing analytics…</div>;

  const kpis = [
    { label: "Acknowledgement Rate", value: `${data.ack_rate}%`, icon: CheckCircle2, tone: "green", id: "an-ack-rate" },
    { label: "Avg Ack Time", value: `${data.avg_ack_hours}h`, icon: Timer, tone: "blue", id: "an-ack-time" },
    { label: "On-Time Delivery", value: data.on_time_pct != null ? `${data.on_time_pct}%` : "—", icon: Truck, tone: "green", id: "an-ontime" },
    { label: "Delayed POs", value: `${data.delayed_pct}%`, icon: AlertTriangle, tone: "red", id: "an-delayed" },
    { label: "Avg Response Time", value: `${data.avg_response_hours}h`, icon: Hourglass, tone: "amber", id: "an-response" },
    { label: "Avg Delivery Delay", value: `${data.avg_delay_days}d`, icon: CalendarClock, tone: "red", id: "an-delay" },
    { label: "Follow-Ups Sent", value: data.followups_sent, icon: MessageSquareText, tone: "slate", id: "an-followups" },
    { label: "Automated Follow-Ups", value: data.automated_followups, icon: Zap, tone: "blue", id: "an-auto" },
    { label: "Manual Effort Saved", value: `${data.effort_saved_hours}h`, icon: Zap, tone: "green", id: "an-effort", sub: "~15 min per automated follow-up" },
    { label: "Open PO Ageing (avg)", value: `${data.open_ageing}d`, icon: Hourglass, tone: "amber", id: "an-ageing" },
    { label: "Due This Week", value: data.due_this_week, icon: CalendarClock, tone: "amber", id: "an-due" },
    { label: "Overdue POs", value: data.overdue, icon: AlertTriangle, tone: "red", id: "an-overdue" },
  ];

  const chartData = data.supplier_table.filter((s) => s.total_pos > 0).slice(0, 8)
    .map((s) => ({ name: s.name.split(" ")[0], onTime: s.on_time_pct ?? 0 }));

  return (
    <div data-testid="analytics-page" className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-semibold tracking-tight">Analytics</h1>
          <p className="text-sm text-muted-foreground">Procurement performance, supplier scorecards and follow-up automation impact.</p>
        </div>
        <a href="/buyer/analytics/spend-visibility" data-testid="spend-visibility-link"
          className="flex items-center gap-2 rounded-lg border bg-card px-4 py-2.5 text-sm font-medium transition-colors hover:bg-slate-50">
          <BarChart3 className="h-4 w-4 text-slate-700" /> Spend Visibility &amp; Analytics
          <span className="text-xs text-muted-foreground">→ spend, savings, supplier dependency &amp; risk</span>
        </a>
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {kpis.map((k, i) => (
          <div key={k.id} className="animate-fade-up" style={{ animationDelay: `${i * 40}ms` }}>
            <StatCard label={k.label} value={k.value} icon={k.icon} tone={k.tone} sub={k.sub} testId={k.id} />
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="rounded-lg border bg-card lg:col-span-2">
          <h2 className="border-b px-5 py-3 font-heading text-base font-semibold">Supplier Performance</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="supplier-performance-table">
              <thead>
                <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="px-5 py-2.5 font-medium">Supplier</th>
                  <th className="px-5 py-2.5 font-medium text-right">Total POs</th>
                  <th className="px-5 py-2.5 font-medium text-right">On-Time %</th>
                  <th className="px-5 py-2.5 font-medium text-right">Delayed</th>
                  <th className="px-5 py-2.5 font-medium text-right">Avg Response</th>
                  <th className="px-5 py-2.5 font-medium text-right">Avg Delay</th>
                </tr>
              </thead>
              <tbody>
                {data.supplier_table.map((s) => (
                  <tr key={s.id} data-testid={`perf-${s.name.replace(/\W+/g, "-").toLowerCase()}`} onClick={() => navigate(`${base}/suppliers/${s.id}`)} className="cursor-pointer border-b last:border-0 hover:bg-slate-50">
                    <td className="px-5 py-3"><p className="font-medium">{s.name}</p><p className="text-xs text-muted-foreground">{s.city}</p></td>
                    <td className="px-5 py-3 text-right font-mono">{s.total_pos}</td>
                    <td className={`px-5 py-3 text-right font-mono font-semibold ${(s.on_time_pct ?? 100) >= 80 ? "text-[#166534]" : "text-[#DC2626]"}`}>{s.on_time_pct != null ? `${s.on_time_pct}%` : "—"}</td>
                    <td className={`px-5 py-3 text-right font-mono ${s.delayed_pos > 0 ? "font-semibold text-[#DC2626]" : ""}`}>{s.delayed_pos}</td>
                    <td className="px-5 py-3 text-right font-mono">{s.avg_response_hours != null ? `${s.avg_response_hours}h` : "—"}</td>
                    <td className="px-5 py-3 text-right font-mono">{s.avg_delay_days != null ? `${s.avg_delay_days}d` : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="rounded-lg border bg-card">
          <h2 className="border-b px-5 py-3 font-heading text-base font-semibold">On-Time Delivery by Supplier</h2>
          <div className="h-80 min-h-80 w-full min-w-0 p-4" data-testid="ontime-chart">
            <ResponsiveContainer width="100%" height="100%" minWidth={200} minHeight={200}>
              <BarChart data={chartData} layout="vertical" margin={{ left: 8, right: 16 }}>
                <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#E2E8F0" />
                <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 11 }} />
                <YAxis type="category" dataKey="name" width={80} tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v) => `${v}%`} />
                <Bar dataKey="onTime" radius={[0, 4, 4, 0]}>
                  {chartData.map((d, i) => <Cell key={i} fill={d.onTime >= 80 ? "#22C55E" : d.onTime >= 50 ? "#F59E0B" : "#EF4444"} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
}
